// エージェントループ。
//
// 流れ:
//   1. 質問 + ツール定義 を LLM へ送る
//   2. LLM が tool_calls を返したら「ブラウザ側で」ツールを実行する
//      （= ログイン中ユーザー自身の権限でFirestoreを読む。RBACがそのまま効く）
//   3. 実行結果を tool メッセージとして LLM へ返す
//   4. LLM が最終的な日本語の回答を返すまで 1〜3 を繰り返す
//
// LLMサーバは業務データに一切触れない。ツールの指示を出すだけ。
//
// 【速度の要】gemma4 は「思考(thinking)」を書き出すモデルで、CPU推論では
// これが生成時間の大半を占める（実測: 思考ありで生成346トークン、思考なしで14トークン）。
// ただし思考を切るとツールを呼べなくなるため、次のように使い分ける:
//   - ツールを選ぶ往復       … 思考ON（必須）
//   - 結果を要約する最終回答 … 思考OFF（実測で品質劣化なし）
// プロンプト自体も短いほど速いので、指示は意図的に簡潔に保つ。
import { WRITE_ENABLED } from '../config';
import { sendChat } from './gateway.service';
import { runTool, toolDefinitions, WRITE_TOOL_NAMES } from '../tools/registry';
import { asWriteSuccess, type WriteToolSuccess } from '../tools/types';
import type { ChatMessage, LlmMessage } from '../types';

/**
 * ツール呼び出しの往復回数の上限。
 * LLM が同じツールを呼び続けて止まらなくなるのを防ぐ（暴走防止）。
 * 書込フローは describe → 顧客解決 → create → 要約 で最低4往復使うため 8。
 */
const MAX_TOOL_ROUNDS = 8;

/**
 * 1メッセージあたりの書込ツール実行数の上限（設計書 §7.5「暴走の上限」）。
 * 幻覚でレコードが量産されるのを防ぐ。超過分は実行せずエラーを返す。
 */
const MAX_WRITES_PER_MESSAGE = 3;

/** モデルが何も言わなかったときの案内（空の吹き出しを出さないための最後の砦）。 */
const EMPTY_REPLY = 'うまく回答をまとめられませんでした。質問を具体的にして、もう一度お試しください。';

/** 今日の日付を YYYY-MM-DD で返す。 */
function today(): string {
  const d = new Date();
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * システムプロンプト。
 * ※ 今日の日付は必須。これが無いと「今月の見積」のような相対表現を解決できず、
 *   モデルはツールを呼ばずに聞き返してしまう（実機で確認済み）。
 * ※ 長いほど毎回の処理時間が増えるため、意図的に簡潔にしている。
 */
function systemPrompt(): string {
  const base = [
    `AIRS販売（販売管理システム）の秘書です。今日は ${today()}。`,
    '「今月」等の相対表現はこの日付を基準に解決すること。',
    '見積・顧客のデータは必ずツールで取得し、推測で答えないこと。0件なら「該当なし」と答える。',
  ];
  const writePolicy = WRITE_ENABLED
    ? [
        // 書込ON: 作成・更新はできるが、削除は依然として物理的に不可能。
        '見積(quote)・顧客(customer)・受注(order)は create_record で作成できる。実行前に必ず describe_resource で書ける項目を確認すること。',
        '取引先は customer / subcontractor / supplier 項目に「名前または顧客コード」をそのまま渡す（IDへの解決はシステムが行う。IDやコードを id 項目に入れない）。',
        '既存レコードの修正は create_record でなく update_record を使う（新しい番号を作らない）。',
        // 実機で「エラーが出たので項目名を教えてください」とユーザーに尋ね返す事象が発生。
        // エラーには「使える項目」が同梱されるので、自力で修正して再試行させる。
        'ツールがエラーを返したら、エラー内の「使える項目」や describe_resource で自分で確認し、修正して再試行する。項目名・ID・番号の調べ方をユーザーに尋ねない。',
        // 帳票番号の体系: 受注番号(0001)の配下に見積(0001-01)が枝番でぶら下がる。
        '帳票番号は「案件番号-枝番」（例: 見積 0002-01 の案件番号は 0002）。番号から案件が分かるので、案件番号をユーザーに聞き返さない。',
        '見積は必ず案件(order)に紐づく。orderCode には案件番号を入れる。該当する案件が無ければ、先に create_record(order) で受注を作り、返ってきた code を orderCode に使う（2段階）。',
        '見積の明細は items 配列（品名・数量・単位・単価）で渡す。金額と税は自動計算される。',
        '納品は quoteCode（見積番号）と deliveryDate だけで継承作成できる（明細・顧客・案件は見積から自動継承。他の情報を聞き返さない）。請求は deliveryCode（納品書番号）だけで継承作成できる（締め日・支払期限は顧客設定から自動計算。発行は人が行う）。注文書・外注・仕入は orderCode/取引先id と items で作成。',
        '会話内の「[実行済み: …]」はツールで完了した操作の記録。完了済みの操作を繰り返したり、やっていないと答えたりしないこと。',
        // 「登録しました」の嘘を防ぐ: 実体の表示はツール実行結果からシステム側が描画する。
        '作成・更新の結果は画面が表示する。ツールが成功を返していないのに「作成した・登録した」と自分の言葉で言わないこと。',
        '削除はできない。依頼されたら「削除は画面から操作してください」と答えること。',
      ]
    : [
        // 実機で「見積のコピーを登録しました」と、実行していない書込を報告する幻覚が発生したため明記。
        // 書込OFFではツールは読取専用で書込は物理的に不可能（registry に書込ツールが存在しない）。
        'あなたは閲覧専用。データの作成・複製・変更・削除は一切できない。依頼されたら「私にはできません。画面から操作してください」と答えること。実行した・登録したと決して言わないこと。',
      ];
  const tail = [
    // ツールの引数は原則すべて任意。指定が無ければ「全件」の意味なので、
    // 絞り込み条件を聞き返さずにそのまま呼ぶこと。小型モデルは引数を埋めようとして
    // 「どの期間ですか？」と聞き返しがちで、これが体感の賢さを最も損なっていた。
    '絞り込み条件の指定が無ければ、引数なしでツールを呼ぶこと。条件をユーザーに聞き返さない。',
    '回答は日本語で簡潔に。金額は3桁区切り、日付は YYYY-MM-DD。',
  ];
  return [...base, ...writePolicy, ...tail].join('\n');
}

/**
 * 表示用の履歴を、LLM へ送る形式に変換する。
 * 過去ターンの書込結果（writes）は「[実行済み: …]」として発言に注入する。
 * ツールの実行結果は履歴に残らないため、これが無いとモデルは自分が
 * 何を作ったかを次のターンで忘れ、同じ操作を繰り返したり聞き返したりする（実機で確認）。
 */
function toLlmMessages(history: ChatMessage[]): LlmMessage[] {
  return [
    { role: 'system', content: systemPrompt() },
    ...history.map((m): LlmMessage => {
      const done = (m.writes ?? [])
        .map((w) => `[実行済み: ${w.label} ${w.code} を${w.kind === 'create' ? '作成' : '更新'}]`)
        .join('\n');
      return { role: m.role, content: done ? `${m.content}\n${done}` : m.content };
    }),
  ];
}

/**
 * runAgent の結果。
 * writes は「実際に書き込まれたレコード」の一覧で、チャットの結果カードは
 * LLM の文章ではなくこの値から描画する（＝作られていなければカードは出ない）。
 */
export interface AgentReply {
  text: string;
  writes: WriteToolSuccess[];
}

/** 書込ツール（create/update_record）の呼び出しか。 */
function isWriteCall(name: string | undefined): boolean {
  return (WRITE_TOOL_NAMES as readonly string[]).includes(name ?? '');
}

/**
 * 会話履歴を渡して、AIの最終回答（日本語）と書込結果を得る。
 * 途中でツールが必要なら、ブラウザ側で実行して自動的に往復する。
 * userId はログイン中ユーザー（書込ツールが「誰の権限で」実行するか）。
 */
export async function runAgent(history: ChatMessage[], userId: string | null = null): Promise<AgentReply> {
  const messages = toLlmMessages(history);
  const tools = toolDefinitions();
  const writes: WriteToolSuccess[] = [];
  let toolsExecuted = false;
  let writeCount = 0;

  for (let round = 0; round < MAX_TOOL_ROUNDS; round += 1) {
    // ツール実行後は「結果を要約するだけ」なので思考を切る（生成が劇的に短くなる）。
    // まだツールを選んでいない段階では思考が必要（切ると呼べなくなる）。
    // ※書込フロー含め、実測プローブで think OFF でも2回目以降のツール選択に成功している。
    const reply = await sendChat(messages, {
      tools,
      ...(toolsExecuted ? { think: false } : {}),
    });

    // ツール不要 → これが最終回答
    if (reply.toolCalls.length === 0) {
      // gemma4 は推論を thinking フィールドへ出し、content を空にすることがある（e4b で実測）。
      // 空のまま返すとチャットに空の吹き出しが出るため、必ず何か言わせる。
      return { text: reply.content.trim() || EMPTY_REPLY, writes };
    }

    // ツール要求を履歴に積む
    messages.push({ role: 'assistant', content: reply.content, tool_calls: reply.toolCalls });

    // ブラウザ側で実行し、結果を tool メッセージとして返す。
    // 書込ツールは1メッセージ MAX_WRITES_PER_MESSAGE 件まで（超過分は実行せずエラーを返す）。
    const results = await Promise.all(
      reply.toolCalls.map((call) => {
        if (isWriteCall(call.function?.name)) {
          if (writeCount >= MAX_WRITES_PER_MESSAGE) {
            return Promise.resolve({
              error: `1メッセージで作成・更新できるのは${MAX_WRITES_PER_MESSAGE}件までです。これ以上は実行できません`,
            });
          }
          writeCount += 1;
        }
        return runTool(call, { userId });
      })
    );
    results.forEach((result) => {
      const success = asWriteSuccess(result);
      if (success) writes.push(success);
      messages.push({ role: 'tool', content: JSON.stringify(result) });
    });
    toolsExecuted = true;
  }

  // 上限まで往復しても最終回答に至らなかった場合
  return { text: EMPTY_REPLY, writes };
}
